import React, { useEffect, useState } from 'react';
import { ShieldCheck, Plus, Pencil, Trash2, X, Shield, UserCheck } from 'lucide-react';
import { useApi } from './DashboardLayout';

export default function AdminsPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', admin_type: 2, assign_rolls_IDs: '', first_name: '', last_name: '' });
  const [feedback, setFeedback] = useState(null);
  const { apiFetch } = useApi();

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const [ar] = await Promise.all([apiFetch('/api/admins')]);
    const ad = await ar?.json();
    if (ad) setAdmins(ad);
    setLoading(false);
  };

  const showFb = (type, text) => { setFeedback({ type, text }); setTimeout(() => setFeedback(null), 4000); };

  const openAdd = () => { setEditAdmin(null); setForm({ email: '', password: '', admin_type: 2, assign_rolls_IDs: '', first_name: '', last_name: '' }); setShowModal(true); };
  const openEdit = (a) => { setEditAdmin(a); setForm({ email: a.email || '', password: '', admin_type: a.type, assign_rolls_IDs: a.assign_rolls_IDs || '', first_name: a.first_name || '', last_name: a.last_name || '' }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editAdmin) await apiFetch(`/api/admins/${editAdmin.id}`, { method: 'PUT', body: JSON.stringify(form) });
    else await apiFetch('/api/admins', { method: 'POST', body: JSON.stringify(form) });
    setShowModal(false); load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Admin wirklich löschen?')) return;
    await apiFetch(`/api/admins/${id}`, { method: 'DELETE' }); load();
  };

  const toggleTwoFA = async (admin) => {
    const newVal = !admin.twofa_enabled;
    const r = await apiFetch(`/api/admins/${admin.id}/twofa`, { method: 'PUT', body: JSON.stringify({ twofa_enabled: newVal }) });
    const d = await r?.json();
    if (d?.status) { showFb('success', d.message); load(); }
    else showFb('error', d?.detail || 'Fehler');
  };

  const impersonate = async (admin) => {
    if (!window.confirm(`Als "${admin.email}" anmelden?`)) return;
    const r = await apiFetch(`/api/auth/impersonate/${admin.id}`, { method: 'POST' });
    const d = await r?.json();
    if (d?.token) {
      localStorage.setItem('bgpr_original_token', localStorage.getItem('bgpr_token'));
      localStorage.setItem('bgpr_original_user_type', localStorage.getItem('bgpr_user_type'));
      localStorage.setItem('bgpr_original_email', localStorage.getItem('bgpr_email'));
      localStorage.setItem('bgpr_impersonating', d.original_admin_email);
      localStorage.setItem('bgpr_token', d.token);
      localStorage.setItem('bgpr_user_type', d.user_type);
      localStorage.setItem('bgpr_email', d.email || '');
      localStorage.setItem('bgpr_user_id', d.user_id);
      window.location.href = '/app';
    } else {
      showFb('error', d?.detail || 'Fehler');
    }
  };

  return (
    <div data-testid="admins-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div><h2>Admin verwalten</h2><p>Benutzer mit Administratorrechten</p></div>
        <button className="btn btn-primary" onClick={openAdd} data-testid="add-admin-btn"><Plus size={16} /> Neuer Admin</button>
      </div>

      {feedback && <div className={`design-feedback ${feedback.type}`} data-testid="admins-feedback">{feedback.text}</div>}

      <div className="card">
        {loading ? <div className="empty-state"><p>Laden...</p></div> : (
          <div className="table-wrap">
            <table data-testid="admins-table">
              <thead><tr><th>Name</th><th>E-Mail</th><th>Typ</th><th>Rollen</th><th>2FA</th><th>Aktionen</th></tr></thead>
              <tbody>
                {admins.map(a => (
                  <tr key={a.id}>
                    <td style={{ fontWeight: 500 }}>{[a.first_name, a.last_name].filter(Boolean).join(' ') || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>–</span>}</td>
                    <td>{a.email}</td>
                    <td><span className={`badge ${a.type === 1 ? 'badge-danger' : 'badge-info'}`}>{a.type === 1 ? 'Super Admin' : 'Admin'}</span></td>
                    <td style={{ color: 'var(--text-secondary)' }}>{(a.roll_names || []).join(', ') || '-'}</td>
                    <td>
                      <button
                        className={`btn btn-sm ${a.twofa_enabled ? 'btn-success' : 'btn-secondary'}`}
                        onClick={() => toggleTwoFA(a)}
                        data-testid={`admin-twofa-${a.id}`}
                        title={a.twofa_enabled ? '2FA deaktivieren' : '2FA aktivieren'}
                        style={{ minWidth: 60 }}
                      >
                        <Shield size={12} /> {a.twofa_enabled ? 'An' : 'Aus'}
                      </button>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="btn-icon edit" onClick={() => openEdit(a)} title="Bearbeiten" data-testid={`admin-edit-${a.id}`}><Pencil size={14} /></button>
                        <button className="btn-icon cert" onClick={() => impersonate(a)} title="Als Benutzer anmelden" data-testid={`admin-impersonate-${a.id}`}><UserCheck size={14} /></button>
                        {a.type === 2 && <button className="btn-icon delete" onClick={() => handleDelete(a.id)} title="Löschen" data-testid={`admin-delete-${a.id}`}><Trash2 size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="admin-modal">
            <div className="modal-header"><h3>{editAdmin ? 'Admin bearbeiten' : 'Neuer Admin'}</h3><button className="modal-close" onClick={() => setShowModal(false)}><X size={18} /></button></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group"><label>Vorname</label><input className="form-input" type="text" value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} /></div>
                  <div className="form-group"><label>Nachname</label><input className="form-input" type="text" value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} /></div>
                </div>
                <div className="form-group"><label>E-Mail</label><input className="form-input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required /></div>
                <div className="form-group"><label>Passwort {editAdmin && '(leer lassen um beizubehalten)'}</label><input className="form-input" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} {...(!editAdmin && { required: true, minLength: 8 })} /></div>
                {/* Typ-Auswahl: Beim Erstellen immer, beim Bearbeiten nur wenn nicht der eigene Master-Account */}
                {(!editAdmin || (editAdmin && editAdmin.id !== parseInt(localStorage.getItem('bgpr_user_id')))) && (
                  <div className="form-group"><label>Typ</label><select className="form-input" value={form.admin_type} onChange={e => setForm(p => ({ ...p, admin_type: parseInt(e.target.value) }))}><option value={2}>Admin</option><option value={1}>Super Admin</option></select></div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" type="button" onClick={() => setShowModal(false)}>Abbrechen</button>
                <button className="btn btn-primary" type="submit" data-testid="admin-submit-btn">{editAdmin ? 'Aktualisieren' : 'Erstellen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
